import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getAccountIntegrationsStrict } from "@common/lib/supabaseClient";
import { AccountIntegration } from "@common/utils/types";

export const accountIntegrationsQueryKey = (accountId: string | undefined) =>
    ["account-integrations", accountId] as const;

const ERROR_POLL_BASE_MS = 30_000;
const ERROR_POLL_JITTER_RATIO = 0.2;

/**
 * ±20% around ERROR_POLL_BASE_MS. Every open tab for every account enters the error state at
 * roughly the same moment during a real fleet-wide outage (the exact scenario EN-1137 is about) and
 * would otherwise retry in near-lockstep every 30s; jitter spreads that back out so recovery doesn't
 * itself look like a thundering herd against a backend that's already struggling.
 *
 * Exported for direct unit testing of the bounds — asserting on the *scheduled* interval through
 * React Query's own internals from the outside is fragile; this is the one thing that actually
 * varies, so it's the one thing tested in isolation.
 */
export const jitteredErrorPollMs = () =>
    ERROR_POLL_BASE_MS + Math.round((Math.random() * 2 - 1) * ERROR_POLL_BASE_MS * ERROR_POLL_JITTER_RATIO);

/**
 * Backs AccountProvider's `pmsIntegrationData`. Uses `getAccountIntegrationsStrict` — which throws
 * on a query error — rather than the forgiving `getAccountIntegrations`, which swallows an error
 * into `[]`. That `[]` is indistinguishable downstream from "this account genuinely has no
 * integration," and is what let a transient Supabase failure permanently grey out/hide the Export
 * button until an unrelated session-object identity change (token refresh, ~hourly) happened to
 * re-run the old fetch-once effect (EN-1137).
 *
 * `refetchInterval` only turns on while the query is erroring, so a failed read keeps retrying
 * roughly every 30s (jittered, see above) until it succeeds — independent of any session change —
 * and adds zero background traffic once healthy. Deliberately not `refetchOnWindowFocus`: the global
 * QueryClient (src/main.tsx) disables that everywhere to avoid a thundering herd of simultaneous
 * refetches from tabs waking up on an unstable connection; overriding it here would reintroduce
 * exactly that, fleet-wide, on any error.
 */
export const useAccountIntegrationsQuery = (
    accountId: string | undefined,
): UseQueryResult<AccountIntegration[], Error> => {
    return useQuery({
        queryKey: accountIntegrationsQueryKey(accountId),
        queryFn: () => getAccountIntegrationsStrict(accountId as string),
        enabled: !!accountId,
        refetchInterval: (query) => (query.state.status === "error" ? jitteredErrorPollMs() : false),
    });
};
